import type { IProject, IWorkAreaAccess } from './types/index.js';

/**
 * A guest's work area (F-0621, AGM-41): one the reader holds nothing in but a guest role (which
 * reads only what is shared with it) or a page shared with them or a role they hold. It is listed
 * so its address resolves, and it is answered as {@link guestWorkAreaListing}: what the address and
 * the switcher need, and nothing of the work area's own.
 *
 * ⛔ Never a member's work area: a reader who holds `work_area.read` there is not a guest, however
 * they came to hold it.
 */
export function isGuestWorkArea(
  project: { access?: Pick<IWorkAreaAccess, 'guest'> | null } | null | undefined,
): boolean {
  return project?.access?.guest === true;
}

/**
 * The fields a guest's listing of a work area carries — an allowlist, so a column added to the work
 * area later is not answered to a guest until it is named here. Its description, policies,
 * visibility, type, parent, owner and earlier addresses are the members' (§21.2 rule 6 of
 * `concept-design-agency-production-and-automation.md`).
 */
export const GUEST_WORK_AREA_FIELDS = [
  'id',
  'name',
  'slug',
  'namespaceId',
  'namespace',
  'namespaceKey',
  'namespaceName',
  'namespaceIsOwn',
  // PX-19: the sender setting as a customer reads it, the role by its display name only.
  'customerSender',
  'access',
  'createdAt',
  'updatedAt',
] as const;

export type GuestWorkAreaListingType = Pick<IProject, (typeof GUEST_WORK_AREA_FIELDS)[number]>;

/**
 * What a guest's listing says of their access, also an allowlist: that they are a guest, and the
 * permissions they hold there, which the web's gates read. ⛔ No member grade, no `canManage` or
 * `canContribute`, and no `holders`: who holds an Owner-only permission is the members' to know.
 */
export const GUEST_WORK_AREA_ACCESS_FIELDS = ['guest', 'permissions'] as const;

export type GuestWorkAreaAccessType = { guest: true; permissions: IWorkAreaAccess['permissions'] };

/**
 * The namespace fields a guest is not told when the namespace is a person's (UXA-4): its path is
 * the owner's handle and its display name the owner's name. Its key, which the address resolves by
 * (`<key>/<slug>`, AC-K9), and its id stay.
 */
export const GUEST_PERSONAL_NAMESPACE_WITHHELD = ['namespace', 'namespaceName'] as const;

/**
 * A work area as a guest is answered it: the allowlisted fields, and the allowlisted access. In a
 * personal namespace (`personalNamespace`) the guest sees an organisation, never a person
 * (UXA-4): the work area is named by its own name and addressed by the namespace's key.
 */
export function guestWorkAreaListing(
  project: Partial<IProject> & Record<string, unknown>,
  access: IWorkAreaAccess,
  { personalNamespace = false }: { personalNamespace?: boolean } = {},
): GuestWorkAreaListingType {
  const listing: Record<string, unknown> = {};
  const withheld: readonly string[] = personalNamespace ? GUEST_PERSONAL_NAMESPACE_WITHHELD : [];
  for (const field of GUEST_WORK_AREA_FIELDS) {
    if (field === 'access') continue;
    if (withheld.includes(field)) {
      listing[field] = null;
      continue;
    }
    if (project[field] !== undefined) listing[field] = project[field];
  }
  const guestAccess: GuestWorkAreaAccessType = {
    guest: true,
    permissions: [...(access.permissions ?? [])],
  };
  listing.access = guestAccess;
  return listing as GuestWorkAreaListingType;
}

/**
 * The work areas a reader is a member of (AGM-51): every listed one but a guest's. What a list that
 * spans work areas reads — All work areas, a namespace scope, move targets, parent pickers — so no
 * aggregate read names a work area the reader holds only as a guest. The switcher and the address
 * resolvers keep the whole list: a guest's work area is an address they may open.
 */
export function memberWorkAreas<T extends Pick<IProject, 'access'>>(projects: readonly T[]): T[] {
  return projects.filter((project) => !isGuestWorkArea(project));
}
